import axios, { AxiosInstance } from 'axios'
import type {
  ApiResponse,
  PaginatedResponse,
  User,
  CreateUserDto,
  UpdateUserDto,
  LoginDto,
  Customer,
  CreateCustomerDto,
  UpdateCustomerDto,
  SearchCustomerDto,
  CustomerApartment,
  CreateCustomerApartmentDto,
  UpdateCustomerApartmentDto,
  SearchCustomerApartmentDto,
  Contract,
  SearchContractDto,
  ContractPayment,
  ContractPhase2PublicInfo,
  ContractPhase2PaymentHistoryResponse,
  ContractPaymentImportResult,
  SearchContractPaymentDto,
  UploadContractPhase2BillResponse,
  Boctham,
  BocthamRegistrationDto,
  BocthamDrawDto,
  BocthamBoardInfo,
  VerifyTokenResponse,
  BulkEmailLogResponse,
  EmailType,
  BocthamTimeInfo,
} from '@/types'

// Normalize base URL to avoid double "/api" and trailing slashes
const normalizeBaseUrl = (rawBaseUrl: string): string => {
  if (!rawBaseUrl) return ''
  let base = rawBaseUrl.trim()
  // remove trailing slash
  if (base.endsWith('/')) base = base.slice(0, -1)
  // if base already ends with /api, strip it because our endpoints already include /api
  if (base.toLowerCase().endsWith('/api')) base = base.slice(0, -4)
  return base
}

class ApiService {
  private api: AxiosInstance

  constructor() {
    const rawBaseUrl = import.meta.env.VITE_API_BASE_URL || ''
    const baseURL = normalizeBaseUrl(rawBaseUrl)
    if (import.meta.env.DEV) {
      // Debug base URL to avoid double /api issues
      // eslint-disable-next-line no-console
      console.log('[ApiService] rawBaseUrl=', rawBaseUrl, 'normalized baseURL=', baseURL)
    }
    this.api = axios.create({
      baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Interceptor to avoid double "/api" in final URL when baseURL already includes it
    this.api.interceptors.request.use(
      (config) => {
        const baseEndsWithApi = (config.baseURL || '').toLowerCase().endsWith('/api')
        if (baseEndsWithApi && typeof config.url === 'string' && config.url.startsWith('/api/')) {
          config.url = config.url.replace(/^\/api\//, '/')
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.log('[ApiService] fixed request url to avoid double /api =>', config.url)
          }
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Check if this is a public endpoint (should not redirect)
          const url = error.config?.url || ''
          const isPublicCustomerEndpoint =
            url.includes('/api/customers/') &&
            (url.includes('/cam-ket-status') ||
              url.includes('/verify-token') ||
              (url.match(/\/api\/customers\/[^/]+$/) &&
                !url.includes('/api/customers/commitments')))

          const isPublicContractPaymentEndpoint = url.includes(
            '/api/contract-payments/public/phase2/'
          )

          // Check if this is a public lottery endpoint (report page)
          // GET /api/boctham (getLotteryEntries) - public endpoint for report page
          // GET /api/boctham/report - public endpoint for report page
          const isPublicLotteryEndpoint =
            url === '/api/boctham' ||
            url.startsWith('/api/boctham?') ||
            url === '/api/boctham/report' ||
            url.startsWith('/api/boctham/report?')

          // Only redirect to login if it's not a public endpoint
          if (
            !isPublicCustomerEndpoint &&
            !isPublicLotteryEndpoint &&
            !isPublicContractPaymentEndpoint
          ) {
            localStorage.removeItem('token')
            localStorage.removeItem('user')
            window.location.href = '/admin/login'
          }
        }
        return Promise.reject(error)
      }
    )
  }

  private mapLotteryEntry(raw: any): Boctham {
    return {
      id: raw.id,
      customerId: raw.customer_id ?? raw.customerId ?? raw.customer?.id ?? 0,
      customer: raw.customer,
      avatarPath: raw.avatar_path ?? raw.avatarPath ?? null,
      tokenKey: raw.token_key ?? raw.tokenKey ?? null,
      tablePoint: raw.table_point ?? raw.tablePoint ?? null,
      tableIndex: raw.table_index ?? raw.tableIndex ?? null,
      ticketNumber: raw.ticket_number ?? raw.ticketNumber ?? null,
      status: typeof raw.status === 'boolean' ? raw.status : !!raw.status,
      createdAt: raw.created_at ?? raw.createdAt,
      dateClick: raw.date_click ?? raw.dateClick ?? null,
      room: raw.room ?? null,
      ticketType: raw.ticket_type ?? raw.ticketType ?? null,
      datCocStatus: raw.dat_coc_status ?? raw.datCocStatus ?? null,
    }
  }

  // Auth endpoints
  async login(credentials: LoginDto): Promise<ApiResponse<{ user: User; access_token: string }>> {
    const response = await this.api.post('/api/auth/login', credentials)
    return response.data
  }

  async logout(): Promise<ApiResponse> {
    const response = await this.api.post('/api/auth/logout')
    return response.data
  }

  async getProfile(): Promise<ApiResponse<User>> {
    const response = await this.api.get('/api/auth/profile')
    return response.data
  }

  // User management endpoints
  async getUsers(page = 1, limit = 10): Promise<ApiResponse<PaginatedResponse<User>>> {
    const response = await this.api.get(`/api/auth/users?page=${page}&limit=${limit}`)
    return response.data
  }

  async getUsersList(): Promise<ApiResponse<Array<{ id: number; name: string }>>> {
    const response = await this.api.get('/api/auth/users/list')
    return response.data
  }

  async createUser(userData: CreateUserDto): Promise<ApiResponse<User>> {
    const response = await this.api.post('/api/auth/users', userData)
    return response.data
  }

  async updateUser(id: number, userData: UpdateUserDto): Promise<ApiResponse<User>> {
    const response = await this.api.put(`/api/auth/users/${id}`, userData)
    return response.data
  }

  async toggleUserStatus(id: number): Promise<ApiResponse<User>> {
    const response = await this.api.patch(`/api/auth/users/${id}/toggle-status`)
    return response.data
  }

  async resetPassword(email: string): Promise<ApiResponse<{ message: string }>> {
    const response = await this.api.post('/api/auth/reset-password', { email })
    return response.data
  }

  // Customer management endpoints
  async getCustomers(
    params: SearchCustomerDto = {}
  ): Promise<ApiResponse<PaginatedResponse<Customer>>> {
    const response = await this.api.get('/api/customers', { params })
    return response.data
  }

  async createCustomer(customerData: CreateCustomerDto): Promise<ApiResponse<Customer>> {
    const response = await this.api.post('/api/customers', customerData)
    return response.data
  }

  async updateCustomer(
    id: number,
    customerData: UpdateCustomerDto
  ): Promise<ApiResponse<Customer>> {
    // Map editable customer fields to backend snake_case payload
    const payload: Record<string, unknown> = {}
    if (typeof customerData.email !== 'undefined') {
      payload.email = customerData.email
    }
    if (typeof customerData.doiTuong !== 'undefined') {
      payload.doi_tuong = customerData.doiTuong
    }
    if (typeof customerData.nhuCau !== 'undefined') {
      payload.nhu_cau = customerData.nhuCau
    }
    if (typeof customerData.tinhTrangHonNhan !== 'undefined') {
      payload.tinh_trang_hon_nhan = customerData.tinhTrangHonNhan
    }
    const response = await this.api.patch(`/api/customers/${id}`, payload)
    return response.data
  }

  async deleteCustomer(id: number): Promise<ApiResponse> {
    const response = await this.api.delete(`/api/customers/${id}`)
    return response.data
  }

  async importCustomers(
    file: File
  ): Promise<ApiResponse<{ inserted: number; updated: number; errors: number }>> {
    const formData = new FormData()
    formData.append('file', file)
    const response = await this.api.post('/api/customers/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  }

  async deleteAllCustomers(): Promise<ApiResponse> {
    const response = await this.api.delete('/api/customers')
    return response.data
  }

  async uploadCustomerCommitment(
    code: string,
    token: string,
    file: File
  ): Promise<ApiResponse<Customer>> {
    const formData = new FormData()
    formData.append('file', file)
    const response = await this.api.post(
      `/api/customers/${encodeURIComponent(code)}/commitments/upload`,
      formData,
      {
        params: { token },
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  }

  async verifyCustomerToken(code: string, token: string): Promise<ApiResponse<Customer>> {
    const response = await this.api.get(`/api/customers/${encodeURIComponent(code)}/verify-token`, {
      params: { token },
    })
    return response.data
  }

  async getCamKetStatus(
    code: string,
    token: string
  ): Promise<
    ApiResponse<{ is_cam_ket: boolean; file_cam_ket: string | null; customer: Customer }>
  > {
    const response = await this.api.get(
      `/api/customers/${encodeURIComponent(code)}/cam-ket-status`,
      {
        params: { token },
      }
    )
    return response.data
  }

  async getCustomerByCodeAndToken(code: string, token: string): Promise<ApiResponse<Customer>> {
    const response = await this.api.get(`/api/customers/${encodeURIComponent(code)}`, {
      params: { token },
    })
    return response.data
  }

  async generateCustomerCommitments(): Promise<
    ApiResponse<{
      total: number
      generated: number
      files: Array<{
        customerId: number
        code: string
        fileName: string
        pdfPath: string
        publicUrl: string
      }>
    }>
  > {
    const response = await this.api.post('/api/customers/commitments/generate')
    return response.data
  }

  async approveCustomerCamKet(id: number): Promise<ApiResponse<Customer>> {
    const response = await this.api.patch(`/api/customers/${id}/approve-cam-ket`)
    return response.data
  }

  async requestResendCustomerCamKet(id: number): Promise<ApiResponse<Customer>> {
    const response = await this.api.post(`/api/customers/${id}/request-resend-cam-ket`)
    return response.data
  }

  async allocateLotteryTimes(): Promise<
    ApiResponse<{
      batch1Count: number
      batch2Count: number
      total: number
    }>
  > {
    const response = await this.api.post('/api/customers/allocate-lottery-times')
    return response.data
  }

  // Lottery management endpoints
  async getLotteryEntries(
    page = 1,
    limit = 10,
    q?: string
  ): Promise<ApiResponse<PaginatedResponse<Boctham>>> {
    const params: Record<string, string | number> = {
      page,
      limit,
    }
    const trimmed = q?.trim()
    if (trimmed) {
      params.q = trimmed
    }
    const response = await this.api.get('/api/boctham', { params })
    const payload = response.data as ApiResponse<PaginatedResponse<any>>

    // Map snake_case from backend to camelCase used in frontend types
    if (payload?.data?.data) {
      const mappedData: Boctham[] = payload.data.data.map((item: any) => this.mapLotteryEntry(item))

      const mappedReport = (() => {
        const report = payload.data.report as any
        if (!report) return undefined
        return {
          totalDraws: report.totalDraws ?? report.total_draws ?? 0,
          completedDraws: report.completedDraws ?? report.completed_draws ?? 0,
          pendingDraws: report.pendingDraws ?? report.pending_draws ?? 0,
          winningTickets: report.winningTickets ?? report.winning_tickets ?? 0,
          blankTickets: report.blankTickets ?? report.blank_tickets ?? 0,
        }
      })()

      const mappedBocthamTime = (() => {
        const timeInfo = payload.data.bocthamTime as any
        if (!timeInfo) return undefined
        return {
          startTimeD1: timeInfo.startTimeD1 ?? timeInfo.start_time_d1 ?? null,
          endTimeD1: timeInfo.endTimeD1 ?? timeInfo.end_time_d1 ?? null,
          startTimeD2: timeInfo.startTimeD2 ?? timeInfo.start_time_d2 ?? null,
          endTimeD2: timeInfo.endTimeD2 ?? timeInfo.end_time_d2 ?? null,
          currentTime: timeInfo.currentTime ?? timeInfo.current_time ?? null,
        } as BocthamTimeInfo
      })()

      return {
        ...payload,
        data: {
          ...payload.data,
          data: mappedData,
          report: mappedReport,
          bocthamTime: mappedBocthamTime,
        },
      }
    }

    return payload as unknown as ApiResponse<PaginatedResponse<Boctham>>
  }

  async exportBocthamEntries(): Promise<{ data: Blob; filename: string }> {
    const response = await this.api.get('/api/boctham/export', {
      responseType: 'blob',
    })

    const disposition = response.headers['content-disposition'] ?? ''
    let filename = `boctham_${new Date().toISOString().split('T')[0]}.xlsx`

    const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/)
    if (match?.[1]) {
      try {
        filename = decodeURIComponent(match[1])
      } catch {
        filename = match[1]
      }
    }

    return { data: response.data, filename }
  }

  async generateLotteryPdf(lotteryId: number): Promise<{ data: Blob; filename: string }> {
    const response = await this.api.get(`/api/boctham/${lotteryId}/pdf`, {
      responseType: 'blob',
    })

    const disposition = response.headers['content-disposition'] ?? ''
    let filename = `lottery_${lotteryId}_${new Date().toISOString().split('T')[0]}.pdf`

    const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/)
    if (match?.[1]) {
      try {
        filename = decodeURIComponent(match[1])
      } catch {
        filename = match[1]
      }
    }

    return { data: response.data, filename }
  }

  async updateDepositStatus(
    lotteryId: number,
    status: 'ok' | 'fail_cu_phap' | 'fail_nop_muon' | 'not_yet'
  ): Promise<ApiResponse<Boctham>> {
    const response = await this.api.patch(`/api/boctham/${lotteryId}/deposit-status`, { status })
    const payload = response.data as ApiResponse<any>
    if (payload?.data) {
      payload.data = this.mapLotteryEntry(payload.data)
    }
    return payload as ApiResponse<Boctham>
  }

  // Public lottery endpoints
  async registerLottery(data: BocthamRegistrationDto): Promise<ApiResponse<Boctham>> {
    const response = await this.api.post('/api/boctham/register', data)
    return response.data
  }

  async getLotteryBoardInfo(code: string, token: string): Promise<ApiResponse<BocthamBoardInfo>> {
    const response = await this.api.get(`/api/boctham/board-info/${code}?token=${token}`)
    return response.data
  }

  async uploadAvatar(
    customerCode: string,
    tokenKey: string,
    file: File
  ): Promise<ApiResponse<{ success: boolean }>> {
    const formData = new FormData()
    formData.append('avatar', file)
    formData.append('code', customerCode)
    formData.append('token', tokenKey)

    const response = await this.api.post('/api/boctham/upload-avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  }

  async verifyToken(
    customerCode: string,
    tokenKey: string
  ): Promise<ApiResponse<VerifyTokenResponse>> {
    const response = await this.api.post('/api/boctham/verify-token', {
      code: customerCode,
      token: tokenKey,
    })
    return response.data
  }

  async drawLottery(data: BocthamDrawDto): Promise<ApiResponse<Boctham>> {
    const response = await this.api.post('/api/boctham/draw', data)
    return response.data
  }

  async getBocthamInfo(
    code: string,
    token: string
  ): Promise<ApiResponse<{ customer: Customer; boctham: any }>> {
    const response = await this.api.get('/api/boctham/info', {
      params: { code, token },
    })
    return response.data
  }

  async getBocthamReport(code: string): Promise<
    ApiResponse<{
      mua: {
        totalCustomersDrawn: number
        totalRooms: number
        totalWinningTickets: number
        totalBlankTickets: number
        drawnRooms: Array<{
          lottery_id: number
          ticket_number: number
          status: boolean
          date_click: string
          created_at: string
          customer: {
            code: string
            name: string
          }
          room: {
            id: number
            toa: string
            name: string
            dien_tich: string
          } | null
        }>
      }
      thue_mua: {
        totalCustomersDrawn: number
        totalRooms: number
        totalWinningTickets: number
        totalBlankTickets: number
        drawnRooms: Array<{
          lottery_id: number
          ticket_number: number
          status: boolean
          date_click: string
          created_at: string
          customer: {
            code: string
            name: string
          }
          room: {
            id: number
            toa: string
            name: string
            dien_tich: string
          } | null
        }>
      }
    }>
  > {
    const response = await this.api.get('/api/boctham/report', {
      params: { code },
    })
    return response.data
  }

  // Email logs
  async getEmailLogs(
    params: { page?: number; limit?: number; email_type?: EmailType; q?: string } = {}
  ): Promise<ApiResponse<PaginatedResponse<any>>> {
    const response = await this.api.get('/api/email-logs', { params })
    return response.data
  }

  async getEmailLogById(id: number): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/email-logs/${id}`)
    return response.data
  }

  async createBulkEmailTokenLogs(data: {
    send_start_time: string
  }): Promise<ApiResponse<BulkEmailLogResponse>> {
    const response = await this.api.post('/api/email-logs/bulk-send-token', data)
    return response.data
  }

  async createBulkEmailQuyCheLogs(data: {
    send_start_time: string
  }): Promise<ApiResponse<BulkEmailLogResponse>> {
    const response = await this.api.post('/api/email-logs/bulk-send-quyche', data)
    return response.data
  }

  async createBulkDepositNotification(data: {
    customer_codes: string
    send_start_time: string
  }): Promise<ApiResponse<BulkEmailLogResponse>> {
    const response = await this.api.post('/api/email-logs/bulk-send-deposit-notification', data)
    return response.data
  }

  async createBulkContractSigningNotification(data: {
    customer_codes: string
    send_start_time: string
  }): Promise<ApiResponse<BulkEmailLogResponse>> {
    const response = await this.api.post(
      '/api/email-logs/bulk-send-contract-signing-notification',
      data
    )
    return response.data
  }

  async createSingleEmailQuyCheLog(data: {
    customerId: number
    send_start_time: string
  }): Promise<ApiResponse<any>> {
    const response = await this.api.post('/api/email-logs/send-quyche', data)
    return response.data
  }

  async resetEmailLogToQueue(id: number): Promise<ApiResponse<any>> {
    const response = await this.api.patch(`/api/email-logs/${id}/reset-queue`)
    return response.data
  }

  // Rooms
  async getRooms(
    params: { page?: number; limit?: number; toa?: string; type?: string; name?: string } = {}
  ): Promise<ApiResponse<PaginatedResponse<any>>> {
    const response = await this.api.get('/api/rooms', { params })
    return response.data
  }

  async updateRoom(
    id: number,
    data: { toa?: string; type?: string; name?: string; dien_tich?: string }
  ): Promise<ApiResponse<any>> {
    const response = await this.api.patch(`/api/rooms/${id}`, data)
    return response.data
  }

  async importRooms(file: File): Promise<ApiResponse<{ inserted: number; updated: number }>> {
    const formData = new FormData()
    formData.append('file', file)
    const response = await this.api.post('/api/rooms/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  }

  async deleteAllRooms(): Promise<ApiResponse<{ deleted: number }>> {
    const response = await this.api.delete('/api/rooms')
    return response.data
  }

  // Customer Apartments endpoints
  async getCustomerApartments(
    params: SearchCustomerApartmentDto = {}
  ): Promise<ApiResponse<PaginatedResponse<CustomerApartment>>> {
    const response = await this.api.get('/api/customers/apartments/all', { params })
    return response.data
  }

  async getCustomerApartmentById(id: number): Promise<ApiResponse<CustomerApartment>> {
    const response = await this.api.get(`/api/customers/apartments/${id}`)
    return response.data
  }

  async createCustomerApartment(
    data: CreateCustomerApartmentDto
  ): Promise<ApiResponse<CustomerApartment>> {
    const response = await this.api.post('/api/customers/apartments', data)
    return response.data
  }

  async updateCustomerApartment(
    id: number,
    data: UpdateCustomerApartmentDto
  ): Promise<ApiResponse<CustomerApartment>> {
    const response = await this.api.patch(`/api/customers/apartments/${id}`, data)
    return response.data
  }

  async deleteCustomerApartment(id: number): Promise<ApiResponse> {
    const response = await this.api.delete(`/api/customers/apartments/${id}`)
    return response.data
  }

  async sendDepositConfirmationEmail(id: number): Promise<ApiResponse> {
    const response = await this.api.post(
      `/api/customers/apartments/${id}/send-deposit-confirmation`
    )
    return response.data
  }

  // Contract endpoints
  async getContracts(
    params: SearchContractDto = {}
  ): Promise<ApiResponse<PaginatedResponse<Contract>>> {
    const response = await this.api.get('/api/contracts', { params })
    return response.data
  }

  async importContracts(
    file: File
  ): Promise<ApiResponse<{ inserted: number; updated: number; errors: number }>> {
    const formData = new FormData()
    formData.append('file', file)
    const response = await this.api.post('/api/contracts/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  }

  async sendContactGuideEmails(): Promise<ApiResponse<{ sent: number; errors: number }>> {
    const response = await this.api.post('/api/contracts/send-contact-guide')
    return response.data
  }

  async sendContactGuideStep2OnlyEmails(): Promise<ApiResponse<{ sent: number; errors: number }>> {
    const response = await this.api.post('/api/contracts/send-contact-guide-step2-only')
    return response.data
  }

  async generateAllRegistrationPdfs(): Promise<
    ApiResponse<{ generated: number; errors: number; files: string[] }>
  > {
    const response = await this.api.post('/api/contracts/generate-all-registration-pdfs')
    return response.data
  }

  async updateAllMaritalStatus(): Promise<
    ApiResponse<{ updated: number; errors: number; skipped: number }>
  > {
    const response = await this.api.post('/api/contracts/update-all-marital-status')
    return response.data
  }

  async updateAllTang(): Promise<
    ApiResponse<{ updated: number; errors: number; skipped: number }>
  > {
    const response = await this.api.post('/api/contracts/update-all-tang')
    return response.data
  }

  async getContractById(id: number): Promise<ApiResponse<Contract>> {
    const response = await this.api.get(`/api/contracts/${id}`)
    return response.data
  }

  async exportContracts(params: SearchContractDto = {}): Promise<{ data: Blob; filename: string }> {
    const response = await this.api.get('/api/contracts/export', {
      params,
      responseType: 'blob',
    })

    const disposition = response.headers['content-disposition'] ?? ''
    let filename = `contracts_${new Date().toISOString().split('T')[0]}.xlsx`

    const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/)
    if (match?.[1]) {
      try {
        filename = decodeURIComponent(match[1])
      } catch {
        filename = match[1]
      }
    }

    return { data: response.data, filename }
  }

  async setContractOK(id: number): Promise<ApiResponse<Contract>> {
    const response = await this.api.post(`/api/contracts/${id}/set-ok`)
    return response.data
  }

  async setContractFail(id: number, reason?: string): Promise<ApiResponse<Contract>> {
    const response = await this.api.post(`/api/contracts/${id}/set-fail`, { reason })
    return response.data
  }

  async updateContractFields(
    id: number,
    data: {
      name?: string
      phone?: string
      email?: string
      cccd_date?: string
      cccd_address?: string
      address_thuong_tru?: string
      address?: string
      cccd?: string
    }
  ): Promise<ApiResponse<Contract>> {
    const response = await this.api.patch(`/api/contracts/${id}/update-fields`, data)
    return response.data
  }

  async getContractPublic(code: string, token: string): Promise<ApiResponse<Contract>> {
    const response = await this.api.get(`/api/contracts/public/${code}/${token}`)
    return response.data
  }

  async updateContractPublic(
    code: string,
    token: string,
    data: {
      cccd_date?: string
      cccd_address?: string
      address_thuong_tru?: string
      address?: string
      phone?: string
      cccd_vnid?: File
      don_dang_ky?: File
      family_member?: any
    }
  ): Promise<ApiResponse<Contract>> {
    const formData = new FormData()

    if (data.cccd_date) {
      formData.append('cccd_date', data.cccd_date)
    }
    if (data.cccd_address !== undefined) {
      formData.append('cccd_address', data.cccd_address)
    }
    if (data.address_thuong_tru !== undefined) {
      formData.append('address_thuong_tru', data.address_thuong_tru)
    }
    if (data.address !== undefined) {
      formData.append('address', data.address)
    }
    if (data.phone !== undefined) {
      formData.append('phone', data.phone)
    }
    if (data.family_member !== undefined && data.family_member !== null) {
      formData.append('family_member', JSON.stringify(data.family_member))
    }

    const files: File[] = []
    if (data.cccd_vnid) {
      files.push(data.cccd_vnid)
    }
    if (data.don_dang_ky) {
      files.push(data.don_dang_ky)
    }

    files.forEach((file) => {
      formData.append('files', file)
    })

    const response = await this.api.post('/api/contracts/public/update', formData, {
      params: { code, token },
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  }

  async getContractPayments(
    params: SearchContractPaymentDto = {}
  ): Promise<ApiResponse<PaginatedResponse<ContractPayment>>> {
    const response = await this.api.get('/api/contract-payments', { params })
    return response.data
  }

  async importContractPayments(file: File): Promise<ApiResponse<ContractPaymentImportResult>> {
    const formData = new FormData()
    formData.append('file', file)
    const response = await this.api.post('/api/contract-payments/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  }

  async sendContractPhase2PaymentEmail(id: number): Promise<ApiResponse<{ emailLogId: number }>> {
    const response = await this.api.post(`/api/contract-payments/${id}/send-phase2-mail`)
    return response.data
  }

  async sendContractPhase2PaymentEmailAll(): Promise<
    ApiResponse<{ mode: 'test' | 'production'; total: number; success: number; failed: number }>
  > {
    const response = await this.api.post('/api/contract-payments/send-phase2-mail-all')
    return response.data
  }

  async getContractPhase2PaymentHistory(
    id: number
  ): Promise<ApiResponse<ContractPhase2PaymentHistoryResponse>> {
    const response = await this.api.get(`/api/contract-payments/${id}/history`)
    return response.data
  }

  async markContractPhase2PaymentSuccess(
    id: number
  ): Promise<ApiResponse<{ paymentId: number; phase2_status: 'success'; emailLogId: number }>> {
    const response = await this.api.post(`/api/contract-payments/${id}/mark-success`)
    return response.data
  }

  async markContractPhase2PaymentFail(
    id: number,
    reason: string
  ): Promise<ApiResponse<{ paymentId: number; phase2_status: 'failed'; emailLogId: number }>> {
    const response = await this.api.post(`/api/contract-payments/${id}/mark-fail`, { reason })
    return response.data
  }

  async getContractPhase2PublicInfo(token: string): Promise<ApiResponse<ContractPhase2PublicInfo>> {
    const response = await this.api.get(
      `/api/contract-payments/public/phase2/${encodeURIComponent(token)}`
    )
    return response.data
  }

  async uploadContractPhase2Bill(
    token: string,
    file: File,
    note?: string
  ): Promise<ApiResponse<UploadContractPhase2BillResponse>> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await this.api.post(
      `/api/contract-payments/public/phase2/${encodeURIComponent(token)}/upload-bill`,
      formData,
      {
        params: note ? { note } : undefined,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  }
}

export const apiService = new ApiService()
export default apiService
